import { Chip, Grid, styled, TableCell, TableRow, Tooltip } from '@mui/material'
import { UTC_TIME_ZONE } from 'components/AvailabilityConstraints/constants'
import { getTheme } from 'components/AvailabilityConstraints/utils'
import { addMinutes } from 'date-fns'
import { useLocale, useResource } from 'hooks'
import { User } from 'hooks/services/users/types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RRule } from 'rrule'
import { AvailabilityConstraintHistory } from 'types'
import { appointmentTypeToDisplayText } from '../../../../../../../util'
import { Author } from './Author'
import { System } from './System'

const DataCell = styled(TableCell)`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.875rem;
`

const AvailabilityTypesCell = styled(TableCell)`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.875rem;
  max-width: 10rem;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

type Props = {
  history: AvailabilityConstraintHistory
  system?: AvailabilityConstraintHistory['created_by']['system']
  user?: User
}

const ruleToOccurrenceDisplayText = (rule: RRule) => {
  const { byweekday: byWeekday, freq: frequency } = rule.options
  const occurrence = new RRule({ byweekday: byWeekday, freq: frequency })

  // TODO: This needs to be i18n'd
  const temp = occurrence.toText().split('on ')[1]
  const tempDays = temp ? temp.split(', ') : []
  const shortDayNames = tempDays.map((day) => day.substring(0, 3))

  return shortDayNames.length > 0 ? shortDayNames.join(', ') : occurrence.toText()
}

const HistoryItem = ({ history, system, user }: Props) => {
  const { locale } = useLocale()
  const { t } = useTranslation()
  const { resource } = useResource()
  const resourceTimeZone = resource?.timezone

  // Format date and time according to locale
  const deltaDateFormatter = useMemo(
    () =>
      Intl.DateTimeFormat(locale, {
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        month: '2-digit',
        timeZone: resourceTimeZone,
        year: 'numeric',
      }),
    [locale, resourceTimeZone]
  )

  const deltaDateDisplayText = deltaDateFormatter.format(new Date(history.created_at))
  const rule = RRule.fromString(history.recurrence_rule)
  const { dtstart: dateStart, until } = rule.options
  const occurrenceText = ruleToOccurrenceDisplayText(rule)

  const startDateText = dateStart?.toLocaleDateString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: UTC_TIME_ZONE,
  })

  const startTimeText = dateStart?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: UTC_TIME_ZONE }) ?? '-'

  const endTimeText =
    addMinutes(dateStart, history.duration_in_minutes).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: UTC_TIME_ZONE,
    }) ?? '-'

  const untilText = until?.toLocaleDateString(locale) ?? '-'
  const appointmentTypeText = history.appointment_types?.map((type) => appointmentTypeToDisplayText?.[type]).join(', ')

  const fallbackUnavailableText =
    appointmentTypeText ?? t('pages.settings.resources.availabilityConstraints.modal.constraints_history.unavailable')

  const fallbackBlockedTimeText =
    appointmentTypeText ?? t('pages.settings.resources.availabilityConstraints.modal.constraints_history.blockedTime')

  const theme = getTheme(history.availability_type, history.appointment_types)
  const rowStyles = { borderLeft: `4px solid ${theme.accent}99` }

  return (
    <TableRow hover key={history.id} style={rowStyles} tabIndex={0}>
      <AvailabilityTypesCell align="left" data-name="Appointment Types">
        <Tooltip placement="top" title={appointmentTypeText ?? fallbackUnavailableText}>
          <span>{appointmentTypeText ?? fallbackBlockedTimeText}</span>
        </Tooltip>
      </AvailabilityTypesCell>
      <DataCell align="center" data-name="Start Date">
        {startDateText}
      </DataCell>
      <DataCell align="center" data-name="From">
        {startTimeText}
      </DataCell>
      <DataCell align="center" data-name="To">
        {endTimeText}
      </DataCell>
      <DataCell align="center" data-name="End Date">
        {untilText}
      </DataCell>
      <DataCell align="left" data-name="Occurrence">
        {occurrenceText}
      </DataCell>
      <DataCell align="left" data-name="Type">
        <Chip
          color={history.delta_type === 'create' ? 'success' : history.delta_type === 'update' ? 'info' : 'error'}
          label={t(`pages.settings.resources.availabilityConstraints.modal.constraints_history.tags.${history.delta_type}`)}
          sx={{ textTransform: 'lowercase' }}
          variant="outlined"
        />
      </DataCell>
      <DataCell align="left" data-name="Created At">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {system ? <System system={system} /> : <Author user={user} />}
          <Grid item>{deltaDateDisplayText}</Grid>
        </div>
      </DataCell>
    </TableRow>
  )
}

export { HistoryItem }
